/**
 * NotificationContext - Global Notification Provider
 * 
 * Provides toast notifications and alerts to the entire app
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

// Notification types
export const NotificationType = {
    SUCCESS: 'success',
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info',
};

// Context
const NotificationContext = createContext(null);

// Notification styles
const typeStyles = {
    success: {
        bg: 'bg-green-50',
        border: 'border-green-400',
        text: 'text-green-800',
        icon: '✓',
    },
    error: {
        bg: 'bg-red-50',
        border: 'border-red-400',
        text: 'text-red-800',
        icon: '✕',
    },
    warning: {
        bg: 'bg-yellow-50',
        border: 'border-yellow-400',
        text: 'text-yellow-800',
        icon: '⚠',
    },
    info: {
        bg: 'bg-blue-50',
        border: 'border-blue-400',
        text: 'text-blue-800',
        icon: 'ℹ',
    },
};

/**
 * NotificationProvider Component
 */
export function NotificationProvider({ children, position = 'top-right', maxNotifications = 5 }) {
    const [notifications, setNotifications] = useState([]);
    const timersRef = useRef({});

    // Position classes
    const positionClasses = {
        'top-right': 'top-4 right-4',
        'top-left': 'top-4 left-4',
        'top-center': 'top-4 left-1/2 -translate-x-1/2',
        'bottom-right': 'bottom-4 right-4',
        'bottom-left': 'bottom-4 left-4',
        'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
    };

    // Clean up timers on unmount
    useEffect(() => {
        return () => {
            Object.values(timersRef.current).forEach(timer => clearTimeout(timer));
        };
    }, []);

    // Add notification
    const addNotification = useCallback((message, type = NotificationType.INFO, options = {}) => {
        const id = Date.now() + Math.random().toString(36).substr(2, 9);
        const duration = options.duration !== undefined ? options.duration : 5000;

        const notification = {
            id,
            message,
            type,
            title: options.title,
            closable: options.closable !== false,
            createdAt: new Date(),
        };

        setNotifications(prev => {
            const next = [...prev, notification];
            // Limit notifications
            if (next.length > maxNotifications) {
                return next.slice(-maxNotifications);
            }
            return next;
        });

        // Auto remove after duration
        if (duration > 0) {
            timersRef.current[id] = setTimeout(() => {
                removeNotification(id);
            }, duration);
        }

        return id;
    }, [maxNotifications]);

    // Remove notification
    const removeNotification = useCallback((id) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
        if (timersRef.current[id]) {
            clearTimeout(timersRef.current[id]);
            delete timersRef.current[id];
        }
    }, []);

    // Clear all
    const clearAll = useCallback(() => {
        setNotifications([]);
        Object.values(timersRef.current).forEach(timer => clearTimeout(timer));
        timersRef.current = {};
    }, []);

    // Convenience methods
    const success = useCallback((message, options = {}) => {
        return addNotification(message, NotificationType.SUCCESS, options);
    }, [addNotification]);

    const error = useCallback((message, options = {}) => {
        return addNotification(message, NotificationType.ERROR, { duration: 8000, ...options });
    }, [addNotification]);

    const warning = useCallback((message, options = {}) => {
        return addNotification(message, NotificationType.WARNING, options);
    }, [addNotification]);

    const info = useCallback((message, options = {}) => {
        return addNotification(message, NotificationType.INFO, options);
    }, [addNotification]);

    // Context value
    const value = {
        notifications,
        addNotification,
        removeNotification,
        clearAll,
        success,
        error,
        warning,
        info,
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
            
            {/* Notification Container */}
            <div className={`fixed z-50 ${positionClasses[position]} space-y-2 pointer-events-none`}>
                {notifications.map(notification => {
                    const style = typeStyles[notification.type] || typeStyles.info;
                    
                    return (
                        <div
                            key={notification.id}
                            className={`
                                pointer-events-auto max-w-sm w-full shadow-lg rounded-lg border-l-4
                                ${style.bg} ${style.border}
                                transform transition-all duration-300 ease-out
                                animate-slide-in
                            `}
                            role="alert"
                        >
                            <div className="p-4">
                                <div className="flex items-start">
                                    <span className={`flex-shrink-0 text-lg ${style.text}`}>
                                        {style.icon}
                                    </span>
                                    <div className="ml-3 flex-1">
                                        {notification.title && (
                                            <p className={`text-sm font-medium ${style.text}`}>
                                                {notification.title}
                                            </p>
                                        )}
                                        <p className={`text-sm ${style.text}`}>
                                            {notification.message}
                                        </p>
                                    </div>
                                    {notification.closable && (
                                        <button
                                            type="button"
                                            className={`ml-4 flex-shrink-0 ${style.text} hover:opacity-70`}
                                            onClick={() => removeNotification(notification.id)}
                                        >
                                            <span className="sr-only">Close</span>
                                            <span className="text-lg">×</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Animation styles */}
            <style>{`
                @keyframes slide-in {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
                .animate-slide-in {
                    animation: slide-in 0.3s ease-out;
                }
            `}</style>
        </NotificationContext.Provider>
    );
}

/**
 * useNotificationContext Hook
 */
export function useNotificationContext() {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotificationContext must be used within a NotificationProvider');
    }
    return context;
}

export default NotificationContext;
